import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ToolCard from '@/components/ui/ToolCard';
import PromptCard from '@/components/ui/PromptCard';
import ResourceCard from '@/components/ui/ResourceCard';
import { useServerData } from '@/hooks/useServerData';
import { useToast } from '@/contexts/ToastContext';
import { useParams } from 'react-router-dom';

const ServerDetail = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { servers, triggerRefresh } = useServerData({ refreshOnMount: true });
  const serverName = useParams().serverName;
  const server = servers.find((server) => server.name === serverName);
  const onRefresh = triggerRefresh;

  if (!server) {
    return <div>没找到服务</div>;
  }

  const handleToolToggle = async (toolName: string, enabled: boolean) => {
    try {
      const { toggleTool } = await import('@/services/toolService');
      const result = await toggleTool(server.name, toolName, enabled);
      if (result.success) {
        showToast(
          t(enabled ? 'tool.enableSuccess' : 'tool.disableSuccess', { name: toolName }),
          'success',
        );
        // Trigger refresh to update the tool's state in the UI
        if (onRefresh) {
          onRefresh();
        }
      } else {
        showToast(result.error || t('tool.toggleFailed'), 'error');
      }
    } catch (error) {
      console.error('Error toggling tool:', error);
      showToast(t('tool.toggleFailed'), 'error');
    }
  };

  const handlePromptToggle = async (promptName: string, enabled: boolean) => {
    try {
      const { togglePrompt } = await import('@/services/promptService');
      const result = await togglePrompt(server.name, promptName, enabled);
      if (result.success) {
        showToast(
          t(enabled ? 'tool.enableSuccess' : 'tool.disableSuccess', { name: promptName }),
          'success',
        );
        // Trigger refresh to update the prompt's state in the UI
        if (onRefresh) {
          onRefresh();
        }
      } else {
        showToast(result.error || t('tool.toggleFailed'), 'error');
      }
    } catch (error) {
      console.error('Error toggling prompt:', error);
      showToast(t('tool.toggleFailed'), 'error');
    }
  };

  const handleToolDescriptionUpdate = (
    _toolName: string,
    _description: string,
    options?: { restored?: boolean },
  ) => {
    showToast(
      options?.restored ? t('tool.restoreDefaultSuccess') : t('tool.descriptionUpdateSuccess'),
      'success',
    );
    if (onRefresh) {
      onRefresh();
    }
  };

  const handlePromptDescriptionUpdate = (
    _promptName: string,
    _description: string,
    options?: { restored?: boolean },
  ) => {
    showToast(
      options?.restored ? t('prompt.restoreDefaultSuccess') : t('prompt.descriptionUpdateSuccess'),
      'success',
    );
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleResourceToggle = async (resourceUri: string, enabled: boolean) => {
    try {
      const { toggleResource } = await import('@/services/resourceService');
      const result = await toggleResource(server.name, resourceUri, enabled);
      if (result.success) {
        showToast(
          t(enabled ? 'tool.enableSuccess' : 'tool.disableSuccess', { name: resourceUri }),
          'success',
        );
        if (onRefresh) {
          onRefresh();
        }
      } else {
        showToast(result.error || t('tool.toggleFailed'), 'error');
      }
    } catch (error) {
      console.error('Error toggling resource:', error);
      showToast(t('tool.toggleFailed'), 'error');
    }
  };

  const handleResourceDescriptionUpdate = async (
    resourceUri: string,
    description: string,
    options?: { restored?: boolean },
  ) => {
    try {
      const { updateResourceDescription, resetResourceDescription } = await import(
        '@/services/resourceService'
      );
      const result = options?.restored
        ? await resetResourceDescription(server.name, resourceUri)
        : await updateResourceDescription(server.name, resourceUri, description);
      if (result.success) {
        showToast(
          options?.restored
            ? t('builtinResources.restoreDefaultSuccess')
            : t('builtinResources.descriptionUpdateSuccess'),
          'success',
        );
        if (onRefresh) {
          onRefresh();
        }
      } else {
        showToast(
          result.error ||
            (options?.restored
              ? t('builtinResources.restoreDefaultFailed')
              : t('builtinResources.descriptionUpdateFailed')),
          'error',
        );
      }
    } catch (error) {
      console.error('Error updating resource description:', error);
      showToast(
        options?.restored
          ? t('builtinResources.restoreDefaultFailed')
          : t('builtinResources.descriptionUpdateFailed'),
        'error',
      );
    }
  };

  return (
    <>
      <div className="text-2xl font-bold text-gray-900 mb-4">{serverName}</div>
      <div
        className="text-sm text-gray-500 break-all mb-4"
        title={server.config?.description || ''}
      >
        {server.config?.description || '暂无描述'}
      </div>

      <div className="bg-white shadow rounded-lg py-6 mb-6 page-card transition-all duration-200">
        {server.tools && (
          <div className="px-4">
            <h6
              className={`font-medium ${server.enabled === false ? 'text-gray-600' : 'text-gray-900'} mb-4`}
            >
              {t('server.tools')}
            </h6>
            <div className="space-y-4">
              {server.tools.length === 0 ? (
                <div className="text-sm text-gray-500 pt-2">暂无工具</div>
              ) : (
                server.tools.map((tool, index) => (
                  <ToolCard
                    key={index}
                    server={server.name}
                    tool={tool}
                    onToggle={handleToolToggle}
                    onDescriptionUpdate={handleToolDescriptionUpdate}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg py-6 mb-6 page-card transition-all duration-200">
        {server.prompts && (
          <div className="px-4">
            <h6
              className={`font-medium ${server.enabled === false ? 'text-gray-600' : 'text-gray-900'} mb-4`}
            >
              {t('server.prompts')}
            </h6>
            <div className="space-y-4">
              {server.prompts.length === 0 ? (
                <div className="text-sm text-gray-500">暂无提示词</div>
              ) : (
                server.prompts.map((prompt, index) => (
                  <PromptCard
                    key={index}
                    server={server.name}
                    prompt={prompt}
                    onToggle={handlePromptToggle}
                    onDescriptionUpdate={handlePromptDescriptionUpdate}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg py-6 page-card transition-all duration-200">
        {server.resources && (
          <div className="px-4">
            <h6
              className={`font-medium ${server.enabled === false ? 'text-gray-600' : 'text-gray-900'} mb-4`}
            >
              {t('nav.resources')}
            </h6>
            {server.resources.length === 0 ? (
              <div className="text-sm text-gray-500">{t('builtinResources.noResources')}</div>
            ) : (
              <div className="space-y-4">
                {server.resources.map((resource, index) => (
                  <ResourceCard
                    key={`${resource.uri}-${index}`}
                    resource={resource}
                    onToggle={handleResourceToggle}
                    onDescriptionUpdate={handleResourceDescriptionUpdate}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ServerDetail;
